import React from 'react';

import Icon, {Icons, Sizes} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import styles from './message.module.styl';

import type {IconProps} from '@pb/kit-component-icon';
import type {BasicIconButtonProps} from '@pb/kit-component-icon-button';
import type {ComponentProps, FC, ReactNode} from 'react';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

export type MessageColor = 'error' | 'info' | 'success' | 'warning';

const COLOR_CLASSES = {
    error: 'message_error',
    success: 'message_success',
    info: 'message_info',
    warning: 'message_warning',
};

type NativeProps = ComponentProps<'div'>;

export interface MessageProps extends NativeProps {
    /**
     * Color of message
     * */
    color: MessageColor,
    /**
     * Message native div properties
     */
    messageProps?: ComponentProps<'div'>,
    /**
     * This icon will appear before text
     */
    iconProps?: IconProps,
    /**
     *  Icon button properties.
     * */
    closeIconButtonProps?: BasicIconButtonProps,
    /**
     * Callback on close message
     */
    onClose?: () => void,
    /**
     * Body of message
     * */
    children: ReactNode,
}

export const Message: FC<MessageProps> = ({
    messageProps,
    iconProps,
    closeIconButtonProps,
    color,
    onClose,
    children,
    ...props
}) => {
    const messageClasses = classNames(
        'message',
        props?.className,
        COLOR_CLASSES[color],
        {'message_without-first-icon': !iconProps},
    );
    const messageClassNames = classNames('message__body', messageProps?.className);

    const onClickCloseMessage = () => {
        onClose?.();
    };

    const defaultRole = color === 'error' ? 'alert' : 'status';

    return (
        <div {...props} role={props?.role ?? defaultRole} className={messageClasses}>
            {iconProps ? (
                <div className={classes.message__icon}>
                    <Icon {...iconProps}/>
                </div>
            ) : null}

            <div {...messageProps} className={messageClassNames}>
                {children}
            </div>

            {onClose ? (
                <BasicIconButton
                    {...closeIconButtonProps}
                    aria-label="Close"
                    iconProps={{
                        type: Icons.CLOSE,
                        size: Sizes.Size_16,
                        ...closeIconButtonProps?.iconProps,
                        className: classNames('message__icon message__icon_close', closeIconButtonProps?.iconProps?.className),
                    }}
                    onClick={onClickCloseMessage}
                />
            ) : null}
        </div>
    );
};

Message.displayName = 'Message';
